import type { RootState } from "@/app/store/store";
import clsx from "clsx";
import type React from "react";
import { useContext, useState } from "react";
import { AiOutlineLogin, AiOutlineLogout } from "react-icons/ai";
import { CgGames } from "react-icons/cg";
import { FaUserGraduate } from "react-icons/fa";
import { FaBlogger } from "react-icons/fa";
import { GiTwoCoins } from "react-icons/gi";
import { MdOutlinePlayLesson } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useNavigate } from "react-router-dom";
import type { PersistPartial } from "redux-persist/es/persistReducer";
import { Nav, Navbar } from "rsuite";
import { GameContext } from "../../../app/context/GameContext";
import { logout } from "../../../entities/Auth/authApi";
import { useLogoutUserMutation } from "../../../entities/User/userApi";
import style from "./Navigation.module.scss";

type NavItemProps = {
	active: string;
	onSelect: (key: string) => void;
	children?: React.ReactNode;
};

export const NavItem: React.FC<NavItemProps> = ({
	active,
	onSelect,
	children,
	...props
}) => (
	<Nav className={style.nav} {...props} activeKey={active} onSelect={onSelect}>
		{children}
	</Nav>
);

export const Navigation = () => {
	const [active, setActive] = useState("home");
	const navigate = useNavigate();
	const { gameModalOpen } = useContext(GameContext);
	const isAuthenticated = useSelector(
		(
			state: RootState &
				PersistPartial & { auth: { isAuthenticated: boolean } },
		) => state.auth.isAuthenticated,
	);
	const { user } = useSelector(
		(
			state: RootState &
				PersistPartial & {
					auth: {
						isAuthenticated: boolean;
						user: {
							userId: string;
							score: number;
						};
					};
				},
		) => state?.auth,
	);
	const dispatch = useDispatch();
	const [logoutUser] = useLogoutUserMutation();

	const navItems = [
		{
			eventKey: "courses",
			icon: <MdOutlinePlayLesson />,
			title: "Courses",
			to: "/courses",
		},
		{ eventKey: "games", icon: <CgGames />, title: "Games", to: "/games" },
		{ eventKey: "blog", icon: <FaBlogger />, title: "Blog", to: "/blog" },
		{
			eventKey: "leadersboard",
			icon: <GiTwoCoins />,
			title: "Leadersboard",
			to: "/leadersboard",
		},
	];

	const handleLogout = async () => {
		await logoutUser().unwrap();
		dispatch(logout());
		navigate("/login");
	};

	const handleSelect = (eventKey: string) => {
		setActive(eventKey);
		const selectedItem = navItems.find((item) => item.eventKey === eventKey);
		if (selectedItem) {
			navigate(selectedItem.to);
		}
	};

	return (
		<div className={clsx(style.layout, { [style.open]: gameModalOpen })}>
			<Navbar appearance="default">
				<Navbar.Brand>
					<Link className={style.brand} to={"/"}>
						<strong>SkillerDome</strong>
					</Link>
				</Navbar.Brand>
				<NavItem active={active} onSelect={handleSelect}>
					{isAuthenticated ? (
						<Nav.Item
							eventKey="logout"
							icon={<AiOutlineLogout />}
							onClick={handleLogout}
						>
							Logout
						</Nav.Item>
					) : (
						<Nav.Item
							eventKey="login"
							icon={<AiOutlineLogin />}
							onClick={() => navigate("/login")}
						>
							Login
						</Nav.Item>
					)}
					{navItems.map((item) => (
						<Nav.Item
							key={item.eventKey}
							eventKey={item.eventKey}
							icon={item.icon}
						>
							{item.title}
						</Nav.Item>
					))}
				</NavItem>
				<Nav pullRight>
					<Nav.Item icon={<GiTwoCoins />}>{user?.score || 0}</Nav.Item>
					{user && (
						<Nav.Item
							onClick={() => navigate("/profile")}
							eventKey={"user"}
							icon={<FaUserGraduate />}
						>
							{user?.firstName} {user?.lastName}
						</Nav.Item>
					)}
				</Nav>
			</Navbar>
			<div className={style.content}>
				<Outlet />
			</div>
		</div>
	);
};
